/**
 * WebAuthn ceremony helpers wrapping `@simplewebauthn/browser` (v13). The BFF
 * returns the option JSON opaquely (typed `unknown` on the wire); we cast it to
 * the library's `*JSON` types right at the call boundary. The returned
 * attestation/assertion response is likewise opaque to the contracts and is
 * forwarded verbatim to the verify endpoints.
 */

import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON
} from '@simplewebauthn/browser'
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService
} from '@simplewebauthn/browser'

/** Drive the attestation ceremony from server-issued creation options. */
export function runRegistration(optionsJSON: unknown): Promise<RegistrationResponseJSON> {
  return startRegistration({ optionsJSON: optionsJSON as PublicKeyCredentialCreationOptionsJSON })
}

/** Drive the assertion ceremony from server-issued request options. */
export function runAuthentication(optionsJSON: unknown): Promise<AuthenticationResponseJSON> {
  return startAuthentication({ optionsJSON: optionsJSON as PublicKeyCredentialRequestOptionsJSON })
}

/**
 * The same ceremony as browser autofill ("conditional UI"): the browser lists this site's passkeys in the e-mail
 * field's suggestions, and the promise settles only when the person picks one. Needs an `<input>` with
 * `autocomplete="username webauthn"` on the page. Starting any other ceremony, or `cancelPasskeyCeremony`, aborts it
 * (an AbortError, which callers ignore).
 */
export function runAutofillAuthentication(optionsJSON: unknown): Promise<AuthenticationResponseJSON> {
  return startAuthentication({
    optionsJSON: optionsJSON as PublicKeyCredentialRequestOptionsJSON,
    useBrowserAutofill: true
  })
}

/** Stop a ceremony in flight (the autofill one, before the page moves on to another path). */
export function cancelPasskeyCeremony(): void {
  WebAuthnAbortService.cancelCeremony()
}

/** WebAuthn at all — passkeys from this device, a phone or a security key. */
export function supportsPasskeys(): boolean {
  return browserSupportsWebAuthn()
}

/** Passkeys offered in the e-mail field's autofill. */
export function supportsPasskeyAutofill(): Promise<boolean> {
  return browserSupportsWebAuthnAutofill().catch(() => false)
}

/** A built-in authenticator (Windows Hello, Touch ID, a phone's screen lock) on THIS device. */
export function hasPlatformAuthenticator(): Promise<boolean> {
  return platformAuthenticatorIsAvailable().catch(() => false)
}

/** A ceremony this page stopped itself — never an error to show. */
export function isAbort(err: unknown): boolean {
  return (
    err instanceof Error && (err.name === 'AbortError' || (err as { code?: string }).code === 'ERROR_CEREMONY_ABORTED')
  )
}

/**
 * Classify a thrown WebAuthn ceremony error so the UI can render a localized,
 * friendly message. The user cancelling the OS prompt surfaces as a
 * DOMException (`NotAllowedError` / `AbortError`) — we don't want to show that
 * as a hard failure. `message` carries the raw error text for the `other`
 * kind, when the browser provided one.
 */
export type PasskeyErrorKind = 'dismissed' | 'already-exists' | 'other'

export function classifyPasskeyError(err: unknown): { kind: PasskeyErrorKind; message?: string } {
  if (err instanceof Error) {
    if (err.name === 'NotAllowedError' || err.name === 'AbortError') return { kind: 'dismissed' }
    if (err.name === 'InvalidStateError') return { kind: 'already-exists' }
    if (err.message) return { kind: 'other', message: err.message }
  }
  return { kind: 'other' }
}
